import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./SetupReferencePages.css";
import LocationsManager from "../managers/LocationsManager";
import { Location } from "../interfaces/LocationsInterface";
import { useSiteContext } from "../SiteContext";
import {
    MapPin,
    Search,
    ChevronRight,
    ChevronLeft,
    ChevronUp,
    ChevronDown,
    X,
    Beaker,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
type LOSortKey = "id" | "name" | "alt";

const PAGE_SIZES = [15, 30, 50, 100];
const DASH = "—";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function cmp(a: string | number, b: string | number): number {
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function pageWindow(p: number, pages: number): (number | "…")[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | "…")[] = [1];
    const lo = Math.max(2, p - 1);
    const hi = Math.min(pages - 1, p + 1);
    if (lo > 2) out.push("…");
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < pages - 1) out.push("…");
    out.push(pages);
    return out;
}

// ─── Component ───────────────────────────────────────────────────────────────
const LocationsPage: React.FC = () => {
    const { setLoading, translator } = useSiteContext();

    // data
    const [data, setData] = useState<Location[] | null>(null);

    // filters / controls
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [sortKey, setSortKey] = useState<LOSortKey>("id");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    // pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(30);

    // ─── Data loading ─────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await LocationsManager.getLocations();
            setData(response || []);
        } catch (err) {
            console.error("Error fetching locations:", err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setLoading unstable from SiteContext
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Reset to page 1 on search/pageSize change (not on sort)
    useEffect(() => { setCurrentPage(1); }, [searchTerm, pageSize]);

    // ─── Filter + sort ────────────────────────────────────────────────────────
    const processed = useMemo<Location[]>(() => {
        if (!data) return [];
        let rows: Location[] = data;
        const q = searchTerm.trim().toLowerCase();
        if (q) {
            rows = rows.filter(l =>
                (l.name || "").toLowerCase().includes(q) ||
                (l.altLocationID || "").toLowerCase().includes(q)
            );
        }
        const keyFn = (l: Location): string | number => {
            switch (sortKey) {
                case "id":   return l.locationID;
                case "name": return (l.name || "").toLowerCase();
                case "alt":  return l.altLocationID || "";
                default:     return l.locationID;
            }
        };
        return [...rows].sort((a, b) => cmp(keyFn(a), keyFn(b)) * (sortDir === "asc" ? 1 : -1));
    }, [data, searchTerm, sortKey, sortDir]);

    // ─── Pagination ───────────────────────────────────────────────────────────
    const total = processed.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const page = Math.min(currentPage, totalPages);
    const startIdx = (page - 1) * pageSize;
    const pageRows = processed.slice(startIdx, startIdx + pageSize);

    const goPage = (n: number) => setCurrentPage(n);

    // ─── Sorting ──────────────────────────────────────────────────────────────
    const handleSort = (key: LOSortKey) => {
        if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
    };

    // ─── Sortable header cell ─────────────────────────────────────────────────
    const Th: React.FC<{ label: string; sk?: LOSortKey }> = ({ label, sk }) => {
        const sortable = !!sk;
        const sorted = sortable && sortKey === sk;
        const desc = sorted && sortDir === "desc";
        return (
            <th
                className={`${sortable ? "sortable" : ""} ${sorted ? "sorted" : ""}`.trim() || undefined}
                onClick={sortable ? () => handleSort(sk!) : undefined}
            >
                <span className="th-in">
                    {label}
                    {sortable && (desc
                        ? <ChevronDown className="caret" size={13} />
                        : <ChevronUp className="caret" size={13} />
                    )}
                </span>
            </th>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PageWrapper title={translator.translate("Locations")} backgroundColor="#f4f5f7">
            <div className="setup-ref">

                {/* breadcrumb */}
                <div className="sr-crumbs">
                    <span>Setup</span>
                    <ChevronRight />
                    <span className="here">Locations</span>
                </div>

                {/* header */}
                <div className="sr-app-head">
                    <div className="sr-logo"><MapPin size={23} /></div>
                    <div>
                        <h1 className="sr-app-title">Locations</h1>
                        <div className="sr-app-sub">
                            Company divisions and offices. Test sites are flagged and excluded from production pickers elsewhere in the app.
                        </div>
                    </div>
                </div>

                {/* filter bar */}
                <div className="sr-toolbar">
                    <div className="sr-field search">
                        <span className="flab">Search</span>
                        <div className="sr-searchwrap">
                            <Search size={17} className="sr-search-icon" />
                            <input
                                className="sr-input"
                                placeholder="Search by name or location ID"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* result line */}
                <div className="sr-result-line">
                    <span>
                        <b>{total}</b> {total === 1 ? "location" : "locations"}
                    </span>
                    {searchTerm.trim() !== "" && (
                        <span className="rl-chip">
                            "{searchTerm.trim()}"
                            <button
                                type="button"
                                className="rl-x"
                                title="Clear search"
                                onClick={() => setSearchTerm("")}
                            >
                                <X size={13} />
                            </button>
                        </span>
                    )}
                </div>

                {/* table */}
                <div className="sr-card">
                    <div className="sr-tbl-wrap">
                        <table className="sr-dt">
                            <thead>
                                <tr>
                                    <Th label="Name" sk="name" />
                                    <Th label="Location ID" sk="alt" />
                                    <th>Address</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data !== null && total === 0 && (
                                    <tr>
                                        <td colSpan={3}>
                                            <div className="sr-empty">
                                                <div className="eic"><MapPin size={26} /></div>
                                                <div className="et">No locations found</div>
                                                <p>
                                                    {searchTerm.trim() !== ""
                                                        ? `No locations match "${searchTerm.trim()}".`
                                                        : "There are no locations configured."
                                                    }
                                                </p>
                                                {searchTerm.trim() !== "" && (
                                                    <button
                                                        type="button"
                                                        className="sr-btn sm"
                                                        onClick={() => setSearchTerm("")}
                                                    >
                                                        <X size={15} /> Clear filters
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {pageRows.map(loc => (
                                    <tr key={loc.locationID}>
                                        {/* Name + optional test tag */}
                                        <td>
                                            <div className="name-cell">
                                                <span className="nm">{loc.name}</span>
                                                {loc.testLocation && (
                                                    <span className="test-tag">
                                                        <Beaker size={11} />Test
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {/* Location ID */}
                                        <td>
                                            {loc.altLocationID
                                                ? <span className="sr-mono strong">{loc.altLocationID}</span>
                                                : <span className="dash">{DASH}</span>
                                            }
                                        </td>
                                        {/* Address */}
                                        <td>
                                            {loc.address
                                                ? loc.address
                                                : <span className="dash">{DASH}</span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* pagination — conditional */}
                {total > pageSize && (
                    <div className="sr-pager">
                        <span className="count">
                            Showing <b>{startIdx + 1}–{startIdx + pageRows.length}</b> of <b>{total}</b>
                        </span>
                        <span className="psize">
                            Rows
                            <select
                                className="sr-select"
                                value={pageSize}
                                onChange={e => {
                                    setPageSize(parseInt(e.target.value, 10));
                                    setCurrentPage(1);
                                }}
                            >
                                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </span>
                        {totalPages > 1 && (
                            <span className="pages">
                                <button
                                    className="pg nav"
                                    disabled={page === 1}
                                    onClick={() => goPage(page - 1)}
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                {pageWindow(page, totalPages).map((n, i) =>
                                    n === "…" ? (
                                        <span key={`e${i}`} className="pg ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={n}
                                            className={`pg${n === page ? " on" : ""}`}
                                            onClick={() => goPage(n as number)}
                                        >
                                            {n}
                                        </button>
                                    )
                                )}
                                <button
                                    className="pg nav"
                                    disabled={page === totalPages}
                                    onClick={() => goPage(page + 1)}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </span>
                        )}
                    </div>
                )}

            </div>
        </PageWrapper>
    );
};

export default LocationsPage;
